'use client';

import type { ReactNode } from 'react';
import { Icon } from '@wistia/ui';
import { styled } from 'styled-components';
import {
  accordionSchemeDark,
  accordionSchemeLight,
  disclosureStyles,
} from '@dvd/styles/disclosure';
import TextLink from './TextLink';
import { CtaProps } from '@types';

type AccordionProps = {
  /**
   * What the H4 says
   */
  headline: string;
  /**
   * Descriptive paragraph
   * - Use a simple string (e.g. "A sentence of words.") for straight copy.
   * - Use HTML to apply simple formatting (e.g. A sentence with an <strong>important</strong> word.)
   */
  description: ReactNode;
  /**
   * CTA options
   */
  cta?: CtaProps;
  /**
   * Color variant of the card.
   *
   * Leave unset to take the variant of the enclosing `AccordionList`, which is
   * what nearly every accordion should do. Set it only to override that list —
   * for a single light card in a dark list, or for an `Accordion` used on its
   * own, which falls back to `light`.
   */
  variant?: 'light' | 'dark';
  /**
   * Whether the accordion should be open on initial render
   */
  defaultOpen?: boolean;
};

const AccordionContainer = styled.li`
  background-color: var(--accordion-bg, ${({ theme }) => theme.colors.white});
  display: flex;
  gap: ${({ theme }) => theme.spacing['1.5']};

  /*
   * Set on the card itself, so an explicit variant beats the set inherited
   * from AccordionList.
   */
  &.light {
    ${accordionSchemeLight}
  }

  &.dark {
    ${accordionSchemeDark}
  }
`;

const AccordionDetails = styled.details`
  ${disclosureStyles}

  flex: 1;
  color: var(--accordion-fg, ${({ theme }) => theme.colors.blue800});
  padding: ${({ theme }) => theme.spacing['1.5']};
  transition: background-color var(--global-motion);

  ${({ theme }) => theme.mq.minWidth.large} {
    padding: ${({ theme }) => `${theme.spacing['1.5']} ${theme.spacing['3']}`};
  }

  &:hover {
    background-color: var(
      --accordion-hover-bg,
      ${({ theme }) => theme.colors.blue100}
    );
  }

  &[open] {
    .expandIcon {
      opacity: 0;
    }

    .collapseIcon {
      opacity: 1;
    }
  }

  .accordionLink {
    display: inline-block;
    color: var(--accordion-link, ${({ theme }) => theme.colors.blue500});
    font-size: ${({ theme }) => theme.typography.fontSize.bodySm};
    padding-block-start: ${({ theme }) => theme.spacing['0.75']};

    ${({ theme }) => theme.mq.minWidth.large} {
      font-size: ${({ theme }) => theme.typography.fontSize.desktop.bodySm};
    }

    &:visited {
      color: var(--accordion-link, ${({ theme }) => theme.colors.blue500});
    }
  }
`;

const AccordionSummary = styled.summary`
  align-items: center;
  display: flex;
  justify-content: space-between;
  position: relative;
  padding-inline-end: ${({ theme }) =>
    theme.spacing['1.5']}; // space for icon, to avoid overlap

  .accordionIcon {
    position: absolute;
    top: 0;
    right: 0;
    margin-bottom: -50%;
    color: var(--accordion-icon, ${({ theme }) => theme.colors.blue500});
    transition: opacity var(--global-motion);
  }

  .collapseIcon {
    opacity: 0;
  }
`;

const AccordionHeadline = styled.h4`
  flex: 80%;
  margin: 0;
`;

// Note: all this padding nonsense is because sometimes the description is a
// string, and rendered as a single <p>, sometimes it's a React Fragment with
// pagaragraphs, sometimes it's a React Fragment without paragraphs
const AccordionDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodySm};
  margin: 0;
  padding-block-start: ${({ theme }) => theme.spacing['0.75']};
  &.longDescription {
    p {
      margin: 0;
      padding-block-start: ${({ theme }) => theme.spacing['0.75']};
    }
    p:first-child {
      padding-block-start: 0;
    }
    /* Remove bottom margin from last element when HTML is used for description. */
    :last-child {
      margin-block-end: 0;
    }
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${({ theme }) => theme.typography.fontSize.desktop.bodySm};
  }
`;

const Accordion = ({
  headline,
  description,
  cta = null,
  variant,
  defaultOpen = false,
}: AccordionProps) => {
  const descriptionIsString = typeof description === 'string';

  return (
    <AccordionContainer className={variant}>
      <AccordionDetails open={defaultOpen}>
        <AccordionSummary>
          <AccordionHeadline>{headline}</AccordionHeadline>
          <Icon
            className="accordionIcon expandIcon"
            role="presentation"
            type="plus"
          />
          <Icon
            className="accordionIcon collapseIcon"
            role="presentation"
            type="minus"
          />
        </AccordionSummary>

        {descriptionIsString ? (
          <AccordionDescription>{description}</AccordionDescription>
        ) : (
          <AccordionDescription as="div" className="longDescription">
            {description}
          </AccordionDescription>
        )}
        {cta ? <TextLink className="accordionLink" cta={cta} /> : undefined}
      </AccordionDetails>
    </AccordionContainer>
  );
};

export default Accordion;
